/**
 * Coach Dashboard Page
 *
 * Dashboard for coaches to manage their profile, clients, and session bookings.
 * Route: /coach/dashboard
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetMyCoachProfileQuery,
  useUpdateMyCoachProfileMutation,
  useGetEngagementsAsCoachQuery,
  useGetBookingsQuery,
  useGetSharedSessionsQuery,
  useScheduleBookingMutation,
  useCompleteBookingMutation,
  useCancelBookingMutation,
  type CoachingEngagement,
  type CoachingBooking,
  type SharedPracticeSession,
  type CoachProfile,
} from '../store/api/humanCoachingApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { getErrorMessage } from '../utils/errorHelpers';
import { mutationOutcome } from '../utils/mutationOutcome';

/** GET /coaches/profile answers 404 for "no coach profile yet" — not a failure. */
export function isNoCoachProfile(error: unknown): boolean {
  return (error as { status?: unknown } | undefined)?.status === 404;
}
import { useGetMyCoachAnalyticsQuery, type CoachAnalytics } from '../store/api/orgAnalyticsApi';

type TabId = 'overview' | 'clients' | 'bookings' | 'sessions' | 'analytics' | 'profile';

export function CoachDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Fetch data
  const {
    data: profile,
    isLoading: loadingProfile,
    isError: profileFailed,
    error: profileError,
    refetch: refetchProfile,
  } = useGetMyCoachProfileQuery();
  const { data: engagementsData, isLoading: loadingEngagements } = useGetEngagementsAsCoachQuery({
    status: ['active'],
  });
  const { data: bookingsData, isLoading: loadingBookings } = useGetBookingsQuery({
    asCoach: true,
    status: ['requested', 'scheduled'],
  });
  const { data: sharedSessionsData, isLoading: loadingSessions } = useGetSharedSessionsQuery({
    limit: 20,
  });
  const { data: analyticsData, isLoading: loadingAnalytics } = useGetMyCoachAnalyticsQuery();

  const isLoading = loadingProfile || loadingEngagements || loadingBookings || loadingSessions || loadingAnalytics;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'clients', label: 'My Clients', count: engagementsData?.engagements?.length },
    { id: 'bookings', label: 'Bookings', count: bookingsData?.bookings?.length },
    { id: 'sessions', label: 'Shared Sessions', count: sharedSessionsData?.sessions?.length },
    { id: 'analytics', label: 'Analytics' },
    { id: 'profile', label: 'Coach Profile' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // A failed load is not "you are not a coach yet" — only a 404 is.
  if (profileFailed && !isNoCoachProfile(profileError)) {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Coach Dashboard</h1>
        <LoadErrorNotice
          error={profileError}
          onRetry={() => void refetchProfile()}
          failedMessage="Your coach profile could not be loaded."
          testIdPrefix="coach-profile"
        />
      </div>
    );
  }

  if (!profile) {
    // PUT /coaches/profile creates the profile when there is none, so setup
    // is the same form as editing — inline, not a separate page.
    return (
      <div className="max-w-2xl mx-auto py-16" data-testid="coach-setup">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">Become a Coach</h1>
        <p className="text-gray-600 mb-8 text-center">
          Set up your coach profile to start accepting clients and offering coaching sessions.
        </p>
        <ProfileTab profile={null} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coach Dashboard</h1>
          <p className="mt-1 text-gray-500">{profile.headline}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              profile.isAcceptingClients
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {profile.isAcceptingClients ? 'Accepting Clients' : 'Not Accepting'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <OverviewTab
            profile={profile}
            engagements={engagementsData?.engagements || []}
            bookings={bookingsData?.bookings || []}
          />
        )}
        {activeTab === 'clients' && (
          <ClientsTab engagements={engagementsData?.engagements || []} />
        )}
        {activeTab === 'bookings' && (
          <BookingsTab bookings={bookingsData?.bookings || []} />
        )}
        {activeTab === 'sessions' && (
          <SharedSessionsTab sessions={sharedSessionsData?.sessions || []} />
        )}
        {activeTab === 'analytics' && analyticsData && (
          <AnalyticsTab analytics={analyticsData} />
        )}
        {activeTab === 'analytics' && !analyticsData && (
          <p className="text-center py-12 text-gray-500">Coaching analytics are not available right now.</p>
        )}
        {activeTab === 'profile' && <ProfileTab profile={profile} />}
      </div>
    </div>
  );
}

// ============================================
// Tab Components
// ============================================

function OverviewTab({
  profile,
  engagements,
  bookings,
}: {
  profile: NonNullable<ReturnType<typeof useGetMyCoachProfileQuery>['data']>;
  engagements: CoachingEngagement[];
  bookings: CoachingBooking[];
}) {
  const pendingBookings = bookings.filter((b) => b.status === 'requested');
  const upcomingBookings = bookings.filter((b) => b.status === 'scheduled');

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Stats Cards */}
      <div className="bg-white rounded-lg border p-6">
        <div className="text-sm text-gray-500">Active Clients</div>
        <div className="mt-1 text-3xl font-bold text-gray-900">{engagements.length}</div>
      </div>
      <div className="bg-white rounded-lg border p-6">
        <div className="text-sm text-gray-500">Pending Requests</div>
        <div className="mt-1 text-3xl font-bold text-orange-600">{pendingBookings.length}</div>
      </div>
      <div className="bg-white rounded-lg border p-6">
        <div className="text-sm text-gray-500">Upcoming Sessions</div>
        <div className="mt-1 text-3xl font-bold text-indigo-600">{upcomingBookings.length}</div>
      </div>

      {/* Rates */}
      <div className="md:col-span-3 bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Rates</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-sm text-gray-500">One-Time Session</div>
            <div className="text-xl font-bold text-gray-900">
              {profile.rateOneTime ? `$${profile.rateOneTime}` : 'Not set'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Monthly Package</div>
            <div className="text-xl font-bold text-gray-900">
              {profile.rateMonthly ? `$${profile.rateMonthly}/mo` : 'Not set'}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Requests */}
      {pendingBookings.length > 0 && (
        <div className="md:col-span-3 bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Session Requests</h3>
          <div className="space-y-3">
            {pendingBookings.slice(0, 3).map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientsTab({ engagements }: { engagements: CoachingEngagement[] }) {
  if (engagements.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>You don't have any active clients yet.</p>
        <p className="mt-2 text-sm">
          Share your coach profile or accept engagement requests to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {engagements.map((engagement) => (
        <div key={engagement.id} className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">
                {engagement.client?.firstName} {engagement.client?.lastName}
              </h3>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {engagement.type.replace('_', ' ')}
              </span>
              <p className="mt-1 text-sm text-gray-500">{engagement.planLabel}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
            <span>
              {engagement.sessionLengthMinutes} min sessions
              {engagement.maxSessionsPerMonth && ` (${engagement.maxSessionsPerMonth}/mo)`}
            </span>
            <span className={engagement.autoShareSessions ? 'text-green-600' : 'text-gray-400'}>
              {engagement.autoShareSessions ? 'Auto-sharing enabled' : 'Manual sharing'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function BookingsTab({ bookings }: { bookings: CoachingBooking[] }) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No pending or upcoming bookings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookings.map((booking) => (
        <BookingCard key={booking.id} booking={booking} showActions />
      ))}
    </div>
  );
}

function BookingCard({
  booking,
  showActions = false,
}: {
  booking: CoachingBooking;
  showActions?: boolean;
}) {
  const [scheduleBooking] = useScheduleBookingMutation();
  const [completeBooking] = useCompleteBookingMutation();
  const [cancelBooking] = useCancelBookingMutation();

  // Inline date/time picker (was a free-text ISO prompt).
  const [scheduling, setScheduling] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const handleSchedule = async () => {
    const scheduledTime = localDateTimeToIso(scheduleAt);
    if (!scheduledTime) {
      setScheduleError('Choose a date and time.');
      return;
    }
    setScheduleError(null);
    try {
      await scheduleBooking({ id: booking.id, scheduledTime }).unwrap();
      setScheduling(false);
      setScheduleAt('');
    } catch (err) {
      setScheduleError(getErrorMessage(err, 'The session could not be scheduled. Please try again.'));
    }
  };

  // Failures are shown on the card; the booking keeps the status the server has.
  const [actionError, setActionError] = useState<string | null>(null);

  const handleComplete = async () => {
    setActionError(null);
    await mutationOutcome(
      completeBooking({ id: booking.id }),
      'The session could not be marked complete. Please try again.',
      setActionError,
    );
  };

  const handleCancel = async () => {
    if (confirm('Are you sure you want to cancel this booking?')) {
      setActionError(null);
      await mutationOutcome(
        cancelBooking(booking.id),
        'The booking could not be cancelled. Please try again.',
        setActionError,
      );
    }
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">
            {booking.client?.firstName} {booking.client?.lastName}
          </h4>
          <p className="text-sm text-gray-500">{booking.durationMinutes} minute session</p>
          {booking.requestedTime && (
            <p className="text-sm text-gray-500">
              Requested: {new Date(booking.requestedTime).toLocaleString()}
            </p>
          )}
          {booking.scheduledTime && (
            <p className="text-sm text-indigo-600 font-medium">
              Scheduled: {new Date(booking.scheduledTime).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
              booking.status === 'requested'
                ? 'bg-orange-100 text-orange-800'
                : booking.status === 'scheduled'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {booking.status}
          </span>
          {showActions && (
            <div className="flex gap-2">
              {booking.status === 'requested' && !scheduling && (
                <button
                  onClick={() => setScheduling(true)}
                  className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Schedule
                </button>
              )}
              {booking.status === 'scheduled' && (
                <button
                  onClick={handleComplete}
                  className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Complete
                </button>
              )}
              {(booking.status === 'requested' || booking.status === 'scheduled') && (
                <button
                  onClick={handleCancel}
                  className="text-xs px-3 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {showActions && booking.status === 'requested' && scheduling && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="booking-schedule-form">
          <label className="text-sm text-gray-700">
            Session time
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="ml-2 rounded-md border-gray-300 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleSchedule()}
            className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => {
              setScheduling(false);
              setScheduleError(null);
            }}
            className="text-xs px-3 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
          >
            Back
          </button>
          {scheduleError && (
            <p className="w-full text-sm text-red-600" role="alert">
              {scheduleError}
            </p>
          )}
        </div>
      )}
      {actionError && (
        <p className="mt-2 text-sm text-red-600" role="alert" data-testid="booking-action-error">
          {actionError}
        </p>
      )}
      {booking.notes && <p className="mt-2 text-sm text-gray-600">{booking.notes}</p>}
    </div>
  );
}

/** A `datetime-local` value (local wall time) as an ISO-8601 instant, or null. */
export function localDateTimeToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

function SharedSessionsTab({ sessions }: { sessions: SharedPracticeSession[] }) {
  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No shared practice sessions yet.</p>
        <p className="mt-2 text-sm">
          When clients enable auto-share, their practice sessions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sessions.map((session) => (
        <div key={session.id} className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                {session.user?.firstName} {session.user?.lastName}
              </h4>
              <p className="text-sm text-gray-500">
                {session.endedAt && new Date(session.endedAt).toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              {session.finalScore !== undefined && (
                <div className="text-2xl font-bold text-indigo-600">{session.finalScore}</div>
              )}
              <span className="text-sm text-gray-500">{session.coachType}</span>
            </div>
          </div>
          <div className="mt-3">
            <Link
              to={`/coach/sessions/${session.id}/review`}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              Review session
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsTab({ analytics }: { analytics: CoachAnalytics }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Active Engagements</h3>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {analytics.engagements.active}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {analytics.engagements.total} total
          </p>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Completed Sessions</h3>
          <div className="mt-2 text-3xl font-bold text-green-600">
            {analytics.sessions.completed}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Scheduled</h3>
          <div className="mt-2 text-3xl font-bold text-blue-600">
            {analytics.sessions.scheduled}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Pending Requests</h3>
          <div className="mt-2 text-3xl font-bold text-orange-600">
            {analytics.sessions.requested}
          </div>
        </div>
      </div>

      {/* Session Status Distribution */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Status</h2>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{analytics.sessions.requested}</div>
            <div className="text-sm text-gray-500">Requested</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{analytics.sessions.scheduled}</div>
            <div className="text-sm text-gray-500">Scheduled</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{analytics.sessions.completed}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{analytics.sessions.cancelled}</div>
            <div className="text-sm text-gray-500">Cancelled</div>
          </div>
        </div>
      </div>

      {/* Average Scores */}
      {analytics.avgScoresAcrossCoachedSessions && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Average Scores (Coached Sessions)
          </h2>
          <div className="grid grid-cols-5 gap-4">
            <ScoreCard
              label="Overall"
              value={analytics.avgScoresAcrossCoachedSessions.overall}
            />
            <ScoreCard
              label="Clarity"
              value={analytics.avgScoresAcrossCoachedSessions.clarity}
            />
            <ScoreCard
              label="Structure"
              value={analytics.avgScoresAcrossCoachedSessions.structure}
            />
            <ScoreCard
              label="Accuracy"
              value={analytics.avgScoresAcrossCoachedSessions.accuracy}
            />
            <ScoreCard
              label="Complexity"
              value={analytics.avgScoresAcrossCoachedSessions.complexity}
            />
          </div>
        </div>
      )}

      {/* Client Breakdown */}
      {analytics.byClient && analytics.byClient.length > 0 && (
        <div className="bg-white rounded-lg border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Client Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Score
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Session
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.byClient.map((client) => (
                  <tr key={client.clientUserId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{client.clientName}</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">
                      {client.completedSessions}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {client.avgOverallScore != null ? (
                        <span
                          className={`font-medium ${
                            client.avgOverallScore >= 70
                              ? 'text-green-600'
                              : client.avgOverallScore >= 50
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {client.avgOverallScore.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-500">
                      {client.lastSessionAt
                        ? new Date(client.lastSessionAt).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreCard({ label, value }: { label: string; value?: number }) {
  const displayValue = value != null ? value.toFixed(1) : '-';
  const color =
    value != null && value >= 70
      ? 'text-green-600'
      : value != null && value >= 50
        ? 'text-yellow-600'
        : 'text-gray-400';

  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <div className={`text-2xl font-bold ${color}`}>{displayValue}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

/** Edits the coach profile, or creates it when `profile` is null (PUT upserts). */
function ProfileTab({ profile }: { profile: CoachProfile | null }) {
  const [updateProfile, { isLoading: isUpdating }] = useUpdateMyCoachProfileMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [formData, setFormData] = useState({
    headline: profile?.headline ?? '',
    bio: profile?.bio || '',
    specialties: profile?.specialties.join(', ') ?? '',
    rateOneTime: profile?.rateOneTime?.toString() || '',
    rateMonthly: profile?.rateMonthly?.toString() || '',
    sessionLengthMinutes: profile?.sessionLengthMinutes.toString() ?? '60',
    maxClients: profile?.maxClients?.toString() || '',
    isAcceptingClients: profile?.isAcceptingClients ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    try {
      await updateProfile({
      headline: formData.headline,
      bio: formData.bio || undefined,
      specialties: formData.specialties
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean),
      rateOneTime: formData.rateOneTime ? parseFloat(formData.rateOneTime) : undefined,
      rateMonthly: formData.rateMonthly ? parseFloat(formData.rateMonthly) : undefined,
      sessionLengthMinutes: parseInt(formData.sessionLengthMinutes),
      maxClients: formData.maxClients ? parseInt(formData.maxClients) : undefined,
      isAcceptingClients: formData.isAcceptingClients,
      }).unwrap();
      setSaved(true);
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Your coach profile could not be saved. Please try again.'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">Headline</label>
        <input
          type="text"
          required
          minLength={3}
          maxLength={255}
          value={formData.headline}
          onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Bio</label>
        <textarea
          rows={4}
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Specialties (comma-separated)
        </label>
        <input
          type="text"
          value={formData.specialties}
          onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
          placeholder="Sales, Pitching, Public Speaking"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">One-Time Rate ($)</label>
          <input
            type="number"
            step="0.01"
            value={formData.rateOneTime}
            onChange={(e) => setFormData({ ...formData, rateOneTime: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Monthly Rate ($)</label>
          <input
            type="number"
            step="0.01"
            value={formData.rateMonthly}
            onChange={(e) => setFormData({ ...formData, rateMonthly: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Session Length (min)</label>
          <input
            type="number"
            value={formData.sessionLengthMinutes}
            onChange={(e) => setFormData({ ...formData, sessionLengthMinutes: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Max Clients</label>
          <input
            type="number"
            value={formData.maxClients}
            onChange={(e) => setFormData({ ...formData, maxClients: e.target.value })}
            placeholder="No maximum"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isAcceptingClients"
          checked={formData.isAcceptingClients}
          onChange={(e) => setFormData({ ...formData, isAcceptingClients: e.target.checked })}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="isAcceptingClients" className="ml-2 block text-sm text-gray-900">
          Currently accepting new clients
        </label>
      </div>

      <div>
        <button
          type="submit"
          disabled={isUpdating}
          className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {isUpdating ? 'Saving...' : profile ? 'Save Profile' : 'Create Coach Profile'}
        </button>
        {saveError && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {saveError}
          </p>
        )}
        {saved && profile && <p className="mt-2 text-sm text-green-700">Profile saved.</p>}
      </div>
    </form>
  );
}

export default CoachDashboardPage;
